import React, { useMemo } from 'react';
import Chart from 'react-apexcharts';

/**
 * 
 * Install dependencies:
 *   npm install react-apexcharts apexcharts
 *
 * Data shape (example):
 * {
 *   "Well-being": {
 *     "Clarity": { coefficient, SE, average_score, ... },
 *     "Psychological Safety": { ... },
 *     ...
 *   },
 *   "Retention": { ... },
 *   "Engagement": { ... }
 * }
 *
 * ROI Formula (per factor, per outcome):
 *   ROI = coefficient * 0.1 + (SE * 1.96 * 0.1 * coefficient)
 *
 * Steps:
 *   1) Aggregate data across outcomes -> average ROI & average score per factor.
 *   2) Convert ROI -> percent ( *100 ), round to 0 decimals.
 *   3) Convert culture score -> percent (assuming base 10, so (score/10)*100 ), round to 0 decimals.
 *   4) We'll split the chart into quadrants around avgX & avgY.
 *   5) Compute minX/maxX/minY/maxY to place quadrant labels in the 'middle' of each quadrant.
 *   6) Reverse the y-axis so bigger scores appear at the top.
 */

const QuadrantROIChart = ({ data }) => {
  // 1) Aggregate the data
  const aggregatedData = useMemo(() => {
    const factorMap = {};

    // data: { "Well-being": {...}, "Retention": {...}, "Engagement": {...} }
    Object.values(data).forEach((outcomeData) => {
      // outcomeData: { "Clarity": {coefficient, SE, average_score}, ... }
      Object.entries(outcomeData).forEach(([factorName, factorInfo]) => {
        const { coefficient, SE, average_score } = factorInfo;
        // ROI for this factor in this outcome
        const roi = coefficient * 0.1 + (SE * 1.96 * 0.1 * coefficient);

        if (!factorMap[factorName]) {
          factorMap[factorName] = { roiSum: 0, scoreSum: 0, count: 0 };
        }
        factorMap[factorName].roiSum += roi;
        factorMap[factorName].scoreSum += average_score;
        factorMap[factorName].count += 1;
      });
    });

    // Convert factorMap into an array of data points for ApexCharts
    return Object.entries(factorMap).map(([factorName, { roiSum, scoreSum, count }]) => {
      // average ROI across outcomes
      const avgROI = roiSum / count;
      // convert ROI to % (rounded 0 decimals)
      const roiPercent = Math.round(avgROI * 100);

      // average score across outcomes
      const avgScore = scoreSum / count;
      // convert to % if base = 10 (rounded 0 decimals)
      const scorePercent = Math.round((avgScore / 10) * 100);

      return {
        factorName,
        x: roiPercent,    // ROI%
        y: scorePercent   // Culture score%
      };
    });
  }, [data]);

  // 2) Single series with all factors
  const series = [
    {
      name: 'Factors',
      data: aggregatedData
    }
  ];

  // 3) Compute average X/Y to define quadrant lines
  const len = aggregatedData.length;
  const avgX = len > 0 
    ? aggregatedData.reduce((sum, d) => sum + d.x, 0) / len 
    : 0;
  const avgY = len > 0
    ? aggregatedData.reduce((sum, d) => sum + d.y, 0) / len
    : 0;

  // 4) Also compute min/max for X/Y to help place quadrant labels
  let minX = Infinity, maxX = -Infinity;
  let minY = Infinity, maxY = -Infinity;
  aggregatedData.forEach((pt) => {
    if (pt.x < minX) minX = pt.x;
    if (pt.x > maxX) maxX = pt.x;
    if (pt.y < minY) minY = pt.y;
    if (pt.y > maxY) maxY = pt.y;
  });

  // Safeguard if there's no data
  if (minX === Infinity) minX = 0;
  if (maxX === -Infinity) maxX = 100;
  if (minY === Infinity) minY = 0;
  if (maxY === -Infinity) maxY = 100;

  /**
   * Because the y-axis is reversed, smaller y = top, bigger y = bottom.
   * Quadrant layout (with reversed Y):
   * 
   *  (x < avgX, y < avgY)  => top-left
   *  (x > avgX, y < avgY)  => top-right
   *  (x < avgX, y > avgY)  => bottom-left
   *  (x > avgX, y > avgY)  => bottom-right
   *
   * We'll position the text near the center of each quadrant:
   *   top-left:    ( (minX+avgX)/2, (minY+avgY)/2 )
   *   top-right:   ( (avgX+maxX)/2, (minY+avgY)/2 )
   *   bottom-left: ( (minX+avgX)/2, (avgY+maxY)/2 )
   *   bottom-right:( (avgX+maxX)/2, (avgY+maxY)/2 )
   */
  const quadrantAnnotations = {
    xaxis: [
      // vertical line at avgX
      {
        x: avgX,
        strokeDashArray: 4,
        borderColor: '#999',
        label: {
          borderColor: '#999',
          style: {
            color: '#fff',
            background: '#999'
          },
          text: `ROI ~ ${Math.round(avgX)}%`
        }
      }
    ],
    yaxis: [
      // horizontal line at avgY
      {
        y: avgY,
        strokeDashArray: 4,
        borderColor: '#999',
        label: {
          borderColor: '#999',
          style: {
            color: '#fff',
            background: '#999'
          },
          text: `Culture ~ ${Math.round(avgY)}%`
        }
      }
    ],
    texts: [
      // Top-left Quadrant
      {
        x: (minX + avgX) / 2,
        y: (minY + avgY) / 2,
        text: 'Opportunity\n(High Culture, Low ROI)',
        textAnchor: 'middle',
        style: {
          fontSize: '12px',
          color: '#333',
          background: 'transparent'
        }
      },
      // Top-right Quadrant
      {
        x: (avgX + maxX) / 2,
        y: (minY + avgY) / 2,
        text: 'Champions\n(High Culture, High ROI)',
        textAnchor: 'middle',
        style: {
          fontSize: '12px',
          color: '#333',
          background: 'transparent'
        }
      },
      // Bottom-left Quadrant
      {
        x: (minX + avgX) / 2,
        y: (avgY + maxY) / 2,
        text: 'Low Priority\n(Low Culture, Low ROI)',
        textAnchor: 'middle',
        style: {
          fontSize: '12px',
          color: '#333',
          background: 'transparent'
        }
      },
      // Bottom-right Quadrant
      {
        x: (avgX + maxX) / 2,
        y: (avgY + maxY) / 2,
        text: 'Potential\n(Low Culture, High ROI)',
        textAnchor: 'middle',
        style: {
          fontSize: '12px',
          color: '#333',
          background: 'transparent'
        }
      }
    ]
  };

  // 5) Chart options
  const options = {
    chart: {
      type: 'scatter',
      toolbar: { show: false },
      width: '100%'
    },
    xaxis: {
      title: { text: 'ROI (%)' },
      labels: {
        formatter: (val) => Math.round(val)
      },
      // You may set min & max if you want some padding
      // min: Math.min(0, minX - 5),
      // max: maxX + 5
    },
    yaxis: {
      reversed: true,
      title: { text: 'Culture Score (%)' },
      labels: {
        formatter: (val) => Math.round(val)
      },
      // min: Math.min(0, minY - 5),
      // max: maxY + 5
    },
    annotations: quadrantAnnotations,
    markers: {
      size: 6
    },
    tooltip: {
      custom: function({ series, seriesIndex, dataPointIndex, w }) {
        const point = w.globals.initialSeries[seriesIndex].data[dataPointIndex];
        return `
          <div style="padding:5px;">
            <strong>Factor:</strong> ${point.factorName}<br/>
            <strong>ROI:</strong> ${point.x}%<br/>
            <strong>Culture:</strong> ${point.y}%
          </div>
        `;
      }
    }
  };

  return (
    <div style={{ width: '100%' }}>
      <Chart 
        options={options}
        series={series}
        type="scatter"
        height={400}
      />
    </div>
  );
};

export default QuadrantROIChart;
